/* eslint-disable no-param-reassign */
import { isNil, isNotNil } from '@flowio/is';
import { DomReference } from '../types';
import { resolveElement } from './elementHelpers';

type LoadingSpinnerOptions = {
  container?: DomReference;
};

const stylesheetId = 'flow-payment-skdk-spinner-styles';
const spinnerClassname = 'flow-sdk-loading-spinner';
const spinnerColor = '#aaa';
let spinnerDom: HTMLDivElement;
let cachedContainer: DomReference;
let relativedContainer = false;

function isHTMLElement(el: Node): el is HTMLElement {
  return el instanceof HTMLElement;
}

function injectSpinnerCss(): void {
  if (document.getElementById(stylesheetId) !== null) {
    return;
  }
  const spinnerStyleEl = document.createElement('style');
  spinnerStyleEl.id = stylesheetId;
  const cssText = document.createTextNode(`
    @keyframes loading-spins {
      0% { transform: rotate(0); }
      100% { transform: rotate(359deg); }
    }
    .${spinnerClassname} {
      background-color: rgba(255,255,255,0.95);
      width: 100%;
      height: 100%;
      min-height: 100px;
      min-width: 100px;
      display: flex;
      justify-content: center;
      align-items: center;
      overflow: hidden;
      position: absolute;
      z-index: 9999999;
    }
    .${spinnerClassname} > div {
      width: 72px;
      height: 72px;
      position: relative;
    }
    .${spinnerClassname} > div > div {
      width: 72px;
      height: 72px;
      border-radius: 50%;
      border: 4px solid #000;
      border-color: ${spinnerColor} transparent ${spinnerColor} transparent;
      animation: loading-spins 0.7s linear infinite;
      box-sizing: border-box;
    }
  `);
  spinnerStyleEl.appendChild(cssText);
  document.head.appendChild(spinnerStyleEl);
}

function getSpinnerDom(): HTMLDivElement {
  if (isNil(spinnerDom)) {
    const spinner = document.createElement('div');
    spinner.className = spinnerClassname;
    const spinnerInside = document.createElement('div');
    const spinnerInnerDiv = document.createElement('div');
    spinnerInside.appendChild(spinnerInnerDiv);
    spinner.appendChild(spinnerInside);
    spinnerDom = spinner;
  }
  return spinnerDom;
}

function ensureContainerStyle(container: HTMLElement): void {
  const containerStyle = getComputedStyle(container);
  // position the container relatively if needed so as the loading spinner overlays correctly.
  if (containerStyle.position === 'static') {
    relativedContainer = true;
    container.style.position = 'relative';
  }
  // set a minimum height so as the loading spinner can be seen even if there's no content to
  // overlay
  if (containerStyle.minHeight) {
    if (parseInt(containerStyle.minHeight, 10) === 0) {
      container.style.minHeight = '100px';
    }
  }
}

export function showLoadingSpinner(opts: LoadingSpinnerOptions = {}): void {
  try {
    injectSpinnerCss();
    const container = resolveElement(opts.container || cachedContainer);
    ensureContainerStyle(container);
    container.appendChild(getSpinnerDom());
    if (opts.container) {
      cachedContainer = opts.container;
    }
  } catch (e) {
    // do nothing
  }
}

export function hideLoadingSpinner(): void {
  if (isNotNil(spinnerDom) && isNotNil(spinnerDom.parentNode)) {
    spinnerDom.parentNode.removeChild(spinnerDom);
    if (relativedContainer && isHTMLElement(spinnerDom.parentNode)) {
      spinnerDom.parentNode.style.position = '';
      relativedContainer = false;
    }
  }
}
